/**
 * LandingFooter — closing section of the landing page.
 *
 * Compact: inline brand row + status pill at top, three info columns
 * (VEGR / CONTACT / LEGAL) underneath, tight bottom row. No emojis. Single
 * brand accent. Privacy / Terms placeholders for the GDPR requirement
 * (CLAUDE.md §9).
 */

import { Mail, MapPin, Phone, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Container } from "@/components/ui/Container";

export function LandingFooter() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-slate-800/60 bg-slate-950 py-14 lg:py-16">
      {/* Soft brand-blue seam separating cream prelaunch above from the dark footer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent"
      />

      <Container>
        {/* Inline brand row */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-2 text-slate-50">
              <Truck className="h-5 w-5 text-brand-400" strokeWidth={1.5} aria-hidden />
              <span className="text-lg font-semibold tracking-tight" translate="no">
                {t("app.name")}
              </span>
            </span>
            <span aria-hidden className="hidden text-slate-700 sm:inline">·</span>
            <span className="text-sm text-slate-400">{t("footer.tagline")}</span>
          </div>

          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-800 bg-slate-900/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-300/90">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-400" aria-hidden />
            {t("footer.status")}
          </span>
        </div>

        {/* Three info columns */}
        <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-4">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {t("footer.linksLabel")}
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-300">
              <li>
                <a href="#kategorier" className="transition-colors hover:text-slate-50">
                  {t("footer.links.categories")}
                </a>
              </li>
              <li>
                <a href="#for-deg" className="transition-colors hover:text-slate-50">
                  {t("footer.links.carriers")}
                </a>
              </li>
              <li>
                <a href="#om-vegr" className="transition-colors hover:text-slate-50">
                  {t("footer.links.about")}
                </a>
              </li>
              <li>
                <a
                  href="#bli-en-av-de-forste"
                  className="transition-colors hover:text-slate-50"
                >
                  {t("footer.links.earlyAccess")}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-5">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {t("footer.contactLabel")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-3">
                <Mail
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-600"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <span className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">
                  {t("footer.contactPlaceholder")}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-600"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <span className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">
                  {t("footer.contactPlaceholder")}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-500"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <div className="flex flex-col">
                  <span className="text-slate-300">{t("footer.address")}</span>
                  <span className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">
                    {t("footer.hours")}
                  </span>
                </div>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {t("footer.legalLabel")}
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-300">
              <li>
                <a href="/privacy" className="transition-colors hover:text-slate-50">
                  {t("footer.legal.privacy")}
                </a>
              </li>
              <li>
                <a href="/terms" className="transition-colors hover:text-slate-50">
                  {t("footer.legal.terms")}
                </a>
              </li>
              <li>
                <a
                  href="#bli-en-av-de-forste"
                  className="transition-colors hover:text-slate-50"
                >
                  {t("footer.legal.support")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-slate-800/60 pt-6 text-xs text-slate-500 md:flex-row md:items-center">
          <span>
            © {year} {t("app.name")} AS. {t("footer.rights")}
          </span>
          <span className="font-mono uppercase tracking-[0.18em]">
            {t("footer.locale")}
          </span>
        </div>
      </Container>
    </footer>
  );
}
